// Palette knife: a rigid, slightly flexible blade dragged across wet paint.
//
// The blade is a line segment (perpendicular to travel by default). Pressure
// lowers it (flex lets it bend down toward the surface). Wet paint standing
// above the blade is scraped up into the knife's load; where the surface is
// below the blade, the load is spread back out, filling hollows to a flat
// plane. Paint the blade pushes past its ends piles up as edge ridges. A
// loaded knife on bare canvas lays a flat slab of paint. Everything moves
// real paint volume and pigment (see PaintDeposition.js).

import { createCarrier, loadCarrier, depositToCell, displaceBody } from '../physics/PaintDeposition.js';
import { markRect, PIGMENT_SLOTS } from '../core/PaintState.js';
import { BODY_PRESENT } from '../physics/ViscousPaint.js';

// Paint film a loaded blade leaves per cell of travel by contact.
const KNIFE_SKIN = 0.12;

export function createPaletteKnife(preset, options = {}) {
  const radius = Math.max(1, Number(options.radius) || 10);
  const knife = {
    preset,
    radius,
    medium: options.medium,
    // A knife carries a generous slab of paint on its underside.
    carrier: createCarrier((options.capacity ?? 1) * radius * radius * 8, options.medium),
    last: null,
    dir: [1, 0],
    stats: { scraped: 0, spread: 0 },
  };
  if (options.mixture) loadCarrier(knife.carrier, options.medium, options.mixture, options.loading ?? 1);
  return knife;
}

export function beginKnifeStroke(knife, state, sample) {
  knife.last = { ...sample };
}

export function continueKnifeStroke(knife, state, sample) {
  const last = knife.last;
  if (!last) {
    beginKnifeStroke(knife, state, sample);
    return;
  }
  const dx = sample.x - last.x;
  const dy = sample.y - last.y;
  const distance = Math.hypot(dx, dy);
  if (distance < 1e-3) return;
  const nx = dx / distance;
  const ny = dy / distance;
  const blend = 1 - Math.exp(-distance / Math.max(1, knife.radius * 0.3));
  let dirX = knife.dir[0] + (nx - knife.dir[0]) * blend;
  let dirY = knife.dir[1] + (ny - knife.dir[1]) * blend;
  const len = Math.hypot(dirX, dirY) || 1;
  dirX /= len;
  dirY /= len;
  knife.dir = [dirX, dirY];
  const steps = Math.max(1, Math.ceil(distance / 0.5));
  for (let s = 1; s <= steps; s += 1) {
    const t = s / steps;
    const pressure = (last.pressure ?? 0.5) + ((sample.pressure ?? 0.5) - (last.pressure ?? 0.5)) * t;
    const step = { cx: last.x + dx * t, cy: last.y + dy * t, pressure, ds: distance / steps, dirX: dirX, dirY: dirY };
    // With a sink attached (GPU backend) the blade step is emitted instead of applied here.
    if (knife.sink) knife.sink(step);
    else applyBlade(knife, state, step.cx, step.cy, step.pressure, step.ds);
  }
  knife.last = { ...sample };
}

function applyBlade(knife, state, cx, cy, pressure, ds) {
  const { preset, radius, carrier } = knife;
  const { width, height } = state;
  const [dirX, dirY] = knife.dir;
  const bladeAngle = preset.bladeAngle || 0;
  // Blade runs across the travel direction, optionally angled.
  const ca = Math.cos(bladeAngle);
  const sa = Math.sin(bladeAngle);
  const bx = -dirY * ca - dirX * sa;
  const by = dirX * ca - dirY * sa;
  const half = (radius * (preset.bladeLength || 2)) / 2;
  const p = Math.max(0, Math.min(1, pressure));
  // Blade height above the surface: high pressure scrapes close to the ground.
  const level = 0.6 * (1 - p) + 0.02;
  const flex = preset.flex ?? 0.3;
  const samples = Math.max(2, Math.ceil(half * 2 / 0.7));
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  // Each cell under the blade is visited once per step, however the blade's
  // samples happen to fall on the grid (otherwise rows alternate 1x/2x).
  const visited = new Set();
  for (let s = 0; s <= samples; s += 1) {
    const along = -half + (2 * half * s) / samples;
    const x = Math.floor(cx + bx * along);
    const y = Math.floor(cy + by * along);
    if (x < 0 || y < 0 || x >= width || y >= height) continue;
    const i = y * width + x;
    if (visited.has(i)) continue;
    visited.add(i);
    // A flexible blade bows: lower in the middle, higher toward the ends.
    const bow = flex * p * (1 - (along / half) ** 2) * 0.25;
    const cellLevel = Math.max(0.01, level - bow) + state.dryHeight[i];
    const top = state.bodyVol[i] + state.dryHeight[i];
    const fill = state.bodyVol[i] > BODY_PRESENT ? state.bodyOpen[i] : 1;
    // Contact transfer: where the blade rides on the surface (not cutting into
    // paint above it), its loaded underside leaves a skin of its paint.
    // The skin is a bounded thickness per unit of travel, not a share of the load.
    if (carrier.body > 1e-6 && top <= cellLevel + 0.05) {
      const skin = Math.min(carrier.body, KNIFE_SKIN * (0.4 + 0.6 * p) * Math.min(1, ds));
      depositToCell(state, i, carrier, skin / carrier.body);
    }
    if (top > cellLevel && state.bodyVol[i] > BODY_PRESENT) {
      // Scrape: take wet paint above the blade into the knife.
      const excess = Math.min(state.bodyVol[i], top - cellLevel) * (preset.scrape ?? 0.8) * fill * Math.min(1, ds);
      const f = excess / state.bodyVol[i];
      takeBody(state, i, carrier, f);
      knife.stats.scraped += excess;
    } else if (carrier.body > 1e-6) {
      // Spread: fill the gap under the blade from the knife's load.
      const gap = cellLevel - top;
      const want = Math.min(gap, carrier.body) * (preset.spread ?? 0.6) * Math.min(1, ds);
      if (want > 0) {
        depositToCell(state, i, carrier, want / Math.max(1e-9, carrier.body));
        knife.stats.spread += want;
      }
    }
    // Water is squeegeed aside as well.
    if (state.water[i] > 0.01) {
      const ahead = clampIndex(state, x + Math.round(dirX), y + Math.round(dirY));
      moveWater(state, i, ahead, 0.5);
    }
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  // Paint pushed past the blade ends forms ridges.
  const ridge = preset.edgeRidge ?? 0.3;
  for (const sign of [-1, 1]) {
    const ex = Math.floor(cx + bx * half * sign * 0.95);
    const ey = Math.floor(cy + by * half * sign * 0.95);
    const ox = Math.floor(cx + bx * (half + 1.5) * sign);
    const oy = Math.floor(cy + by * (half + 1.5) * sign);
    if (ex < 0 || ey < 0 || ex >= width || ey >= height || ox < 0 || oy < 0 || ox >= width || oy >= height) continue;
    displaceBody(state, ey * width + ex, oy * width + ox, ridge * Math.min(1, ds));
  }
  if (maxX >= minX) markRect(state, minX - 3, minY - 3, maxX + 3, maxY + 3, true);
}

function clampIndex(state, x, y) {
  const cx = Math.max(0, Math.min(state.width - 1, x));
  const cy = Math.max(0, Math.min(state.height - 1, y));
  return cy * state.width + cx;
}

function moveWater(state, i, j, f) {
  if (i === j) return;
  const ratio = Math.min(1, f);
  const w = state.water[i] * ratio;
  state.water[i] -= w;
  state.water[j] += w;
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
    const a = state.wetPig[k][i] * ratio;
    state.wetPig[k][i] -= a;
    state.wetPig[k][j] += a;
  }
}

function takeBody(state, i, carrier, f) {
  const frac = Math.min(1, f);
  const b = state.bodyVol[i] * frac;
  state.bodyVol[i] -= b;
  carrier.body += b;
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
    const a = state.bodyPig[k][i] * frac;
    if (a > 0) {
      state.bodyPig[k][i] -= a;
      carrier.pigB[k] += a;
    }
  }
}
